import {
  getDiscountInfo,
  isCreditMemoSelectable,
  QuickbooksCreditMemo,
  QuickbooksInvoice,
  SupplierIntegrationType,
} from '@dill/dill-shared';

interface DistributedInvoice {
  invoice: QuickbooksInvoice;
  amountToPay: number;
  discountAmount: number;
  isPartialPay: boolean;
}

interface DistributionResult {
  distributedInvoices: DistributedInvoice[];
  /** Credit memos to send with the payment. Each one is applied in full. */
  appliedCredits: QuickbooksCreditMemo[];
  totalCreditsUsed: number;
  /** Cash left over once every open invoice is cleared — goes out as an open payment. */
  excessAmount: number;
  totalInvoiceAmount: number;
  totalDiscount: number;
}

const round2 = (value: number) => Number(value.toFixed(2));

const balanceOf = (invoice: QuickbooksInvoice) => invoice.balance ?? invoice.amount ?? 0;

/**
 * The "invoice date" shown in the invoice tables, for invoices and credit memos alike.
 * Due date is the fallback so a document without one still lands in a stable spot.
 */
const invoiceDateOf = (document: { createdAt?: Date | null; dueDate?: Date | null }) => {
  const date = document.createdAt ?? document.dueDate;
  return date ? new Date(date).getTime() : Infinity;
};

const byInvoiceDate = (
  a: { createdAt?: Date | null; dueDate?: Date | null },
  b: { createdAt?: Date | null; dueDate?: Date | null }
) => {
  const dateDiff = invoiceDateOf(a) - invoiceDateOf(b);
  if (dateDiff !== 0) return dateDiff;
  const aDue = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
  const bDue = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
  return aDue - bDue;
};

type TimelineEntry =
  | {
      type: 'INVOICE';
      document: QuickbooksInvoice;
      balance: number;
      discountAmount: number;
      payoff: number;
    }
  | { type: 'CREDIT'; document: QuickbooksCreditMemo; amount: number };

interface DistributionParams {
  fixedAmount: number;
  openInvoices: QuickbooksInvoice[];
  openCreditMemos?: QuickbooksCreditMemo[];
  integrationType?: SupplierIntegrationType | null;
  domainId?: string | null;
  discountEligible?: boolean;
}

/** What the bank is charged for the invoice leg: what the invoices absorb, less the credits. */
const netCashCharged = (result: DistributionResult) =>
  round2(result.totalInvoiceAmount - result.totalDiscount - result.totalCreditsUsed);

function distributeOnce({
  fixedAmount,
  openInvoices,
  openCreditMemos = [],
  integrationType,
  domainId,
  discountEligible,
}: DistributionParams): DistributionResult {
  const payableInvoices = openInvoices.filter((invoice) => balanceOf(invoice) > 0);

  if (!payableInvoices.length || fixedAmount <= 0) {
    return {
      distributedInvoices: [],
      appliedCredits: [],
      totalCreditsUsed: 0,
      excessAmount: fixedAmount,
      totalInvoiceAmount: 0,
      totalDiscount: 0,
    };
  }

  // If payment method is not eligible for discounts, skip discount calculation
  const shouldCalculateDiscount = discountEligible !== false;
  const discountFor = (invoice: QuickbooksInvoice, amount: number) => {
    if (!shouldCalculateDiscount) return 0;
    const { discountAmount } = getDiscountInfo(
      { editedPayment: amount, invoiceOriginal: invoice },
      integrationType,
      domainId
    );
    // Most integrations hand back `invoiceDiscountAmount` straight from the ERP, unchecked
    // against the balance. Clamped here so a bad one cannot make a payoff negative — that would
    // give the walk below money to spend that the buyer never paid — or send the server an
    // amountPaid under zero.
    return Math.min(Math.max(discountAmount, 0), amount);
  };

  const eligibleCredits = openCreditMemos.filter(
    (credit) =>
      (credit.remainingCredit ?? 0) > 0 &&
      credit.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
      // Same gate the server uses to decide whether it will apply credits at all. Sending a
      // credit it ignores would bill the buyer for the credited amount in cash.
      isCreditMemoSelectable(credit.recordType, integrationType)
  );

  const timeline: TimelineEntry[] = [
    ...payableInvoices.map((document): TimelineEntry => {
      const balance = balanceOf(document);
      const discountAmount = discountFor(document, balance);
      return {
        type: 'INVOICE',
        document,
        balance,
        discountAmount,
        payoff: balance - discountAmount,
      };
    }),
    ...eligibleCredits.map(
      (document): TimelineEntry => ({
        type: 'CREDIT',
        document,
        amount: document.remainingCredit ?? 0,
      })
    ),
  ].sort((a, b) => {
    const dateDiff = byInvoiceDate(a.document, b.document);
    if (dateDiff !== 0) return dateDiff;
    // A credit dated the same day as an invoice is there to cover it.
    return (a.type === 'CREDIT' ? 0 : 1) - (b.type === 'CREDIT' ? 0 : 1);
  });

  // What the invoices *below* each position still cost to clear. A credit is worth taking only
  // up to this: anything past it is credit the payment has nothing left to apply it to.
  const payoffBelow: number[] = new Array(timeline.length).fill(0);
  for (let index = timeline.length - 2; index >= 0; index -= 1) {
    const next = timeline[index + 1];
    payoffBelow[index] = round2(
      payoffBelow[index + 1] + (next.type === 'INVOICE' ? next.payoff : 0)
    );
  }

  const distributedInvoices: DistributedInvoice[] = [];
  const appliedCredits: QuickbooksCreditMemo[] = [];
  let cashLeft = fixedAmount;
  let creditLeft = 0;
  let totalCreditsUsed = 0;
  let totalInvoiceAmount = 0;
  let totalDiscount = 0;

  // Credit is spent before cash so that whatever is left at the end is cash, not an unusable
  // scrap of credit being handed to the buyer as an open payment.
  const spend = (amount: number) => {
    const fromCredit = Math.min(creditLeft, amount);
    creditLeft = round2(creditLeft - fromCredit);
    cashLeft = round2(cashLeft - (amount - fromCredit));
  };

  timeline.forEach((entry, index) => {
    if (entry.type === 'CREDIT') {
      const stillOwedBelow = payoffBelow[index];
      // The cash already clears everything below, so leave the credit on account.
      if (round2(cashLeft + creditLeft) >= stillOwedBelow) return;
      // Credits go out whole: a partly used one would let the server apply more credit than this
      // split budgets for, and the buyer's bank would be debited less than they entered.
      if (round2(creditLeft + entry.amount) > stillOwedBelow) return;

      appliedCredits.push(entry.document);
      creditLeft = round2(creditLeft + entry.amount);
      totalCreditsUsed = round2(totalCreditsUsed + entry.amount);
      return;
    }

    const available = round2(creditLeft + cashLeft);
    if (available <= 0) return;

    if (available >= entry.payoff) {
      // An invoice whose own discount already covers its balance has nothing left to pay, and a
      // line the bank is charged nothing for is not a payment. Leave it off and move on.
      if (entry.payoff <= 0) return;

      // Pay this invoice in full
      spend(entry.payoff);
      distributedInvoices.push({
        invoice: entry.document,
        amountToPay: entry.balance,
        discountAmount: entry.discountAmount,
        isPartialPay: false,
      });
      totalInvoiceAmount += entry.balance;
      totalDiscount += entry.discountAmount;
    } else {
      // Partial pay: what is available covers part of the balance, so the discount is
      // recalculated for that smaller amount
      const partialDiscount = discountFor(entry.document, available);
      if (round2(available - partialDiscount) <= 0) return;

      spend(available);
      distributedInvoices.push({
        invoice: entry.document,
        amountToPay: available,
        discountAmount: partialDiscount,
        isPartialPay: true,
      });
      totalInvoiceAmount += available;
      totalDiscount += partialDiscount;
    }
  });

  return {
    distributedInvoices,
    appliedCredits,
    totalCreditsUsed,
    excessAmount: round2(Math.max(cashLeft, 0)),
    totalInvoiceAmount: round2(totalInvoiceAmount),
    totalDiscount: round2(totalDiscount),
  };
}

/**
 * Spreads a fixed payment over a buyer's open documents, oldest invoice date first.
 *
 * Invoices and credit memos sit on one timeline, and the payment works down it in date order: a
 * credit only covers the invoices below it, never the older ones above it. So a credit that is
 * newer than an invoice cannot jump the queue to pay it, and a credit newer than every open
 * invoice stays on account.
 */
export function distributeFixedAmountToInvoices(params: DistributionParams): DistributionResult {
  let credits = params.openCreditMemos ?? [];

  // Credits are sized against the invoices below them, so the bank charge should already come
  // out positive. If some edge still lands it under zero — a discount taken on a partial
  // payment that credit had all but covered, say — drop the newest credit and redistribute
  // rather than hand the server a payment that owes the buyer money.
  for (;;) {
    const result = distributeOnce({ ...params, openCreditMemos: credits });
    if (netCashCharged(result) >= 0 || !result.appliedCredits.length) return result;

    const newest = result.appliedCredits[result.appliedCredits.length - 1];
    const remaining = credits.filter((credit) => credit !== newest);
    if (remaining.length === credits.length) return result;
    credits = remaining;
  }
}
